import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Menu, MenuItem } from '@mui/material';
import type { UsageData } from '../../hooks/useUsage';

interface NavbarProps {
  userName: string;
  onSignOut: () => void;
  usage: UsageData | null;
  usageLoading: boolean;
}

const NavbarContainer = styled.header`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 18px;
  padding: 0 28px;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
  height: 56px;
  box-sizing: border-box;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Keyline = styled.div`
  color: var(--t4);
  font-size: 11px;
  letter-spacing: 0.2px;

  @media (max-width: 700px) {
    display: none;
  }
`;

const UserChip = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 14px 4px 4px;
  border-radius: 100px;
  background: var(--s2);
  border: none;
  cursor: pointer;
  transition: background 120ms;
  font-family: inherit;

  &:hover,
  &:focus {
    background: var(--s3);
    outline: none;
  }
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--teal), #00d4ff);
  display: grid;
  place-items: center;
  color: #fff;
  font-weight: 600;
  font-size: 11px;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-size: 12.5px;
  font-weight: 500;
  color: var(--t1);
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function getInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Exported for tests — same shapes as the previous Navbar.
export function shouldShowUsageBar(usageLoading: boolean, usage: UsageData | null): boolean {
  return !usageLoading && usage !== null && usage.limit !== null;
}

export function isExceeded(usage: UsageData | null): boolean {
  return usage !== null && usage.limit !== null && usage.used >= usage.limit;
}

const Navbar: React.FC<NavbarProps> = ({ userName, onSignOut }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  return (
    <NavbarContainer>
      <Keyline>Press ⌘K to search anything</Keyline>

      <UserChip
        aria-controls={open ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <Avatar>{getInitials(userName)}</Avatar>
        <UserName>{userName}</UserName>
      </UserChip>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate('/subscription');
          }}
        >
          Subscription
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            navigate('/settings');
          }}
        >
          Settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onSignOut();
          }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </NavbarContainer>
  );
};

export default Navbar;
